import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../../../hooks/useAtomCallbackMemoized';
import { roomTokenAtom } from '../../../video-sdk';
import { VideoPlatform } from '../../../video-sdk/videoPlatform';
import { zoomTokenAtom } from '../../../video-sdk/zoom/state';

/**
 * Returns a callback that provides the video platform of the room that the
 * waiting client user is being admitted to, based on which room tokens are
 * present. This should also correspond to the provider's video platform, since
 * the provider is the host.
 *
 * Returns `null` if no room tokens are present.
 *
 * > *Note: As of 2026-04-01, this is only used to determine which admission
 * > flow to use. Once the admission flow is unified (TREE-533), this hook may
 * > be deprecated.*
 */
export function useGetVideoPlatformOfAdmittedRoomCallback() {
  const getZoomToken = useAtomCallbackMemoized((get) => get(zoomTokenAtom), []);
  const getTwilioToken = useAtomCallbackMemoized((get) => get(roomTokenAtom), []);

  return useCallback(
    ({ zoomTokenOverride, twilioTokenOverride }: { zoomTokenOverride?: string; twilioTokenOverride?: string } = {}) => {
      const zoomToken = zoomTokenOverride ?? getZoomToken();
      const twilioToken = twilioTokenOverride ?? getTwilioToken();

      if (zoomToken) {
        return VideoPlatform.Zoom;
      }

      if (twilioToken) {
        return VideoPlatform.Twilio;
      }

      return null;
    },
    [getTwilioToken, getZoomToken],
  );
}

/**
 * Reactive wrapper around {@link useGetVideoPlatformOfAdmittedRoomCallback}
 */
export function useVideoPlatformOfAdmittedRoom() {
  const zoomToken = useAtomValue(zoomTokenAtom);
  const twilioToken = useAtomValue(roomTokenAtom);
  const getVideoPlatformOfAdmittedRoom = useGetVideoPlatformOfAdmittedRoomCallback();

  return getVideoPlatformOfAdmittedRoom({ zoomTokenOverride: zoomToken, twilioTokenOverride: twilioToken });
}
